import { ISelectItem, Select } from 'components/UI/Select/Select'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/UI/command'
import { cn } from 'components/UI/cn'
import { useListProductsCatalog } from 'services/products'
import { Check, ChevronDown, X } from 'lucide-react'

import type { SettingsField as SettingsFieldSchema } from './agentTypeMetadata'

interface ProductItem {
  label: string
  value: number
}

interface Props {
  field: SettingsFieldSchema
  value: unknown
  onChange: (value: unknown) => void
  campaignProductId?: number | null
}

export function SettingsField({
  field,
  value,
  onChange,
  campaignProductId,
}: Props) {
  if (field.type === 'multiselect_products') {
    return (
      <ProductMultiSelect
        field={field}
        value={(value as number[]) ?? []}
        onChange={onChange}
        campaignProductId={campaignProductId}
      />
    )
  }
  if (field.type === 'select') {
    return <SelectField field={field} value={value} onChange={onChange} />
  }
  return <TextareaField field={field} value={value} onChange={onChange} />
}

function TextareaField({
  field,
  value,
  onChange,
}: {
  field: SettingsFieldSchema
  value: unknown
  onChange: (value: unknown) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg">
        {field.label}
        {field.required && <span className="text-error"> *</span>}
      </label>
      <textarea
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={field.placeholder ?? undefined}
        className="mt-1 w-full rounded-md border border-line-form px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-1 focus:ring-line-focus"
      />
      {field.helper && (
        <p className="mt-1 text-xs text-fg-secondary">{field.helper}</p>
      )}
    </div>
  )
}

function ProductMultiSelect({
  field,
  value,
  onChange,
  campaignProductId,
}: {
  field: SettingsFieldSchema
  value: number[]
  onChange: (value: number[]) => void
  campaignProductId?: number | null
}) {
  const { data } = useListProductsCatalog({
    page: 1,
    page_size: 1000,
    sort: 'name',
  })
  const items: ProductItem[] = (data?.results ?? []).map((p) => ({
    label: p.name ?? `Product #${p.id}`,
    value: p.id,
  }))
  const selectedProducts = value.flatMap((productId) => {
    const item = items.find((product) => product.value === productId)
    return item ? [item] : []
  })
  const selectedSet = new Set(value)
  const triggerLabel = (() => {
    if (selectedProducts.length === 0) return null
    const firstProduct = selectedProducts[0]
    if (!firstProduct) return null
    if (selectedProducts.length === 1) return firstProduct.label
    return `${firstProduct.label} + ${selectedProducts.length - 1} more`
  })()

  const toggleProduct = (productId: number) => {
    if (selectedSet.has(productId)) {
      onChange(value.filter((id) => id !== productId))
      return
    }
    onChange([...value, productId])
  }

  const removeProduct = (productId: number) => {
    onChange(value.filter((id) => id !== productId))
  }

  const sortedItems = [...items].sort((a, b) => {
    const aSelected = selectedSet.has(a.value)
    const bSelected = selectedSet.has(b.value)
    if (aSelected !== bSelected) return aSelected ? -1 : 1
    const aDefault = a.value === campaignProductId
    const bDefault = b.value === campaignProductId
    if (aDefault !== bDefault) return aDefault ? -1 : 1
    return a.label.localeCompare(b.label)
  })

  return (
    <div>
      <label className="block text-sm font-medium text-fg">
        {field.label}
        {field.required && <span className="text-error"> *</span>}
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="mt-1 flex w-full items-center justify-between gap-2 rounded-md border border-line-form bg-surface px-3 py-2 text-left text-sm text-fg hover:bg-surface-hover focus:outline-none focus:ring-1 focus:ring-line-focus"
          >
            <span
              className={cn(
                'min-w-0 flex-1 truncate',
                !triggerLabel && 'text-fg-secondary'
              )}
            >
              {triggerLabel ?? 'Select products'}
            </span>
            <ChevronDown className="size-4 shrink-0 text-fg-secondary" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] max-w-none p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search products..." />
            <CommandList>
              <CommandEmpty>No products found.</CommandEmpty>
              <CommandGroup>
                {sortedItems.map((item) => {
                  const isSelected = selectedSet.has(item.value)
                  const isCampaignDefault = item.value === campaignProductId
                  return (
                    <CommandItem
                      key={item.value}
                      value={`${item.label} ${item.value}`}
                      onSelect={() => toggleProduct(item.value)}
                    >
                      <div className="flex w-full items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm text-fg">
                            {item.label}
                          </div>
                          {isCampaignDefault && (
                            <div className="text-xs text-fg-secondary">
                              Campaign default
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="size-4 shrink-0 text-brand" />
                        )}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedProducts.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {selectedProducts.map((product) => (
            <div
              key={product.value}
              className="flex min-h-9 items-center justify-between gap-2 rounded-md border border-line bg-surface px-2.5 py-1.5"
            >
              <div className="min-w-0">
                <div className="truncate text-sm text-fg">{product.label}</div>
                {product.value === campaignProductId && (
                  <div className="text-xs text-fg-secondary">
                    Campaign default
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeProduct(product.value)}
                className="rounded p-1 text-fg-secondary hover:bg-surface-hover hover:text-fg"
                aria-label={`Remove ${product.label}`}
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      {field.helper && (
        <p className="mt-1 text-xs text-fg-secondary">{field.helper}</p>
      )}
      {field.required && value.length === 0 && (
        <p className="mt-1 text-xs text-error">
          Select at least one product for this agent.
        </p>
      )}
    </div>
  )
}

function SelectField({
  field,
  value,
  onChange,
}: {
  field: SettingsFieldSchema
  value: unknown
  onChange: (value: unknown) => void
}) {
  const items: ISelectItem<string | number>[] = (field.options ?? []).map(
    (opt) => ({ label: opt.label, value: opt.value })
  )
  return (
    <div>
      <label className="block text-sm font-medium text-fg">
        {field.label}
        {field.required && <span className="text-error"> *</span>}
      </label>
      <div className="mt-1">
        <Select<string | number>
          items={items}
          selected={value as string | number | undefined}
          onSelect={(v) => onChange(v)}
          nullSelectionLabel="Select…"
          matchTriggerWidth
        />
      </div>
      {field.helper && (
        <p className="mt-1 text-xs text-fg-secondary">{field.helper}</p>
      )}
    </div>
  )
}
